import { decodeWpRenderedText, isRecord, unwrapResponseJson } from './wpApi'

export type CardIndexItem = { id: string; title: string }

export type CardJsonV1<TElement = unknown> = {
  version: 1
  props: Record<string, unknown>
  frontElements: TElement[]
  backElements: TElement[]
  post?: {
    title?: string
  }
}

export type CardsListPage<TJson = CardJsonV1> = {
  page: number
  perPage: number
  total: number
  items: CardIndexItem[]
  cards: Array<{ id: string; json: TJson }>
}

export const normalizeCardJsonV1 = <TElement = unknown>(
  json: unknown,
): CardJsonV1<TElement> | null => {
  const payload = unwrapResponseJson(json)
  if (!isRecord(payload)) return null

  const version = payload.version
  const props = (payload as Record<string, unknown>).props
  const frontElements = (payload as Record<string, unknown>).frontElements
  const backElements = (payload as Record<string, unknown>).backElements
  const post = (payload as Record<string, unknown>).post

  if (version !== 1) return null
  if (!isRecord(props)) return null
  if (!Array.isArray(frontElements) || !Array.isArray(backElements)) return null

  return {
    version: 1,
    props: props as Record<string, unknown>,
    frontElements: frontElements as TElement[],
    backElements: backElements as TElement[],
    post: isRecord(post)
      ? {
          title:
            typeof post.title === 'string' && post.title
              ? decodeWpRenderedText(post.title)
              : undefined,
        }
      : undefined,
  }
}

export const normalizeCardsListResponse = <TJson = CardJsonV1>(
  json: unknown,
  normalizeJson?: (value: unknown) => TJson | null,
): CardsListPage<TJson> | null => {
  const unwrapped = unwrapResponseJson(json)
  if (!isRecord(unwrapped)) return null

  const items = (unwrapped as Record<string, unknown>).items
  if (!Array.isArray(items)) return null

  const page = (unwrapped as Record<string, unknown>).page
  const perPage = (unwrapped as Record<string, unknown>).perPage
  const total = (unwrapped as Record<string, unknown>).total

  const parsed: CardIndexItem[] = []
  const parsedCards: Array<{ id: string; json: TJson }> = []

  for (const item of items) {
    if (!isRecord(item)) continue
    const id = (item as Record<string, unknown>).id
    const title = (item as Record<string, unknown>).title
    const cardJson = (item as Record<string, unknown>).json
    if (typeof id === 'string' && typeof title === 'string') {
      parsed.push({ id, title: title ? decodeWpRenderedText(title) : '' })
    }
    if (typeof id === 'string' && cardJson !== undefined && normalizeJson) {
      const doc = normalizeJson(cardJson)
      if (doc) parsedCards.push({ id, json: doc })
    }
  }

  const normalizedPage = typeof page === 'number' && Number.isFinite(page) ? Math.max(1, page) : 1
  const normalizedPerPage =
    typeof perPage === 'number' && Number.isFinite(perPage) ? Math.max(1, perPage) : parsed.length
  const normalizedTotal =
    typeof total === 'number' && Number.isFinite(total) ? Math.max(0, total) : parsed.length

  return {
    page: normalizedPage,
    perPage: normalizedPerPage,
    total: normalizedTotal,
    items: parsed,
    cards: parsedCards,
  }
}
